import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowDownToLine, ArrowUpFromLine, ChevronDown, ChevronRight, Layers, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExpiryCell, fmtDate, fmtQty } from "./stockUtils";

function LotAdjustDialog({ lot, onOpenChange, onDone }) {
  const [qty, setQty] = useState(lot?.quantity ?? "");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Informe o motivo do ajuste");
      return;
    }
    try {
      await api.post("/sanitary/movements", { item_id: lot.id, type: "adjust", quantity: Number(qty) || 0, reason });
      toast.success("Saldo do lote ajustado");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha no ajuste");
    }
  };
  return (
    <Dialog open={!!lot} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-sm" data-testid="lot-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Ajustar saldo do lote</DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            {lot?.name} · {lot?.batch ? `lote ${lot.batch}` : "lote s/ nº"} · val. {fmtDate(lot?.expires_at)}. Use para
            inventário/contagem — a saída normal é feita pelo botão "Saída" do produto (PVPS).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Novo saldo ({lot?.unit})</Label>
            <Input type="number" step="0.01" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 bg-white" data-testid="lot-adjust-qty" autoFocus />
          </div>
          <div>
            <Label>Motivo *</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contagem de inventário, perda, quebra…" className="mt-1 bg-white" data-testid="lot-adjust-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="lot-adjust-submit">Salvar ajuste</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LotsPanel({ product, onLabel, onAdjust, onDeleteLot, canManage }) {
  const lots = product.lots || [];
  if (lots.length === 0) {
    return <div className="px-4 py-3 text-xs text-stone-500 font-body italic">Nenhum lote com saldo. Use "Entrada" para registrar um lote.</div>;
  }
  return (
    <div className="px-4 py-3" data-testid={`product-lots-${product.id}`}>
      <div className="text-[11px] uppercase tracking-wider text-stone-500 font-body mb-2 flex items-center gap-1.5">
        <Layers className="h-3 w-3" /> Lotes em ordem PVPS — o 1º da lista é o próximo a sair
      </div>
      <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Lote</TableHead>
              <TableHead>Produção</TableHead>
              <TableHead>Validade</TableHead>
              <TableHead>Saldo</TableHead>
              <TableHead>Responsável</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lots.map((lot, i) => (
              <TableRow key={lot.id} className={lot.quantity <= 0 ? "opacity-50" : ""} data-testid={`lot-row-${lot.id}`}>
                <TableCell className="text-xs font-body text-stone-500">
                  {i === 0 && lot.quantity > 0 ? <Badge className="bg-emerald-100 text-emerald-800 border-none text-[10px]">próximo</Badge> : `${i + 1}º`}
                </TableCell>
                <TableCell className="font-body text-sm font-semibold">{lot.batch || <span className="text-stone-400">s/ nº</span>}</TableCell>
                <TableCell className="font-body text-xs text-stone-600">{fmtDate(lot.production_date)}</TableCell>
                <TableCell className="font-body text-xs text-stone-600"><ExpiryCell expiresAt={lot.expires_at} compact /></TableCell>
                <TableCell className="font-body text-sm tabular-nums">
                  <b>{fmtQty(lot.quantity)}</b> {lot.unit}
                  {lot.lot_quantity != null && lot.lot_quantity !== lot.quantity && (
                    <span className="text-stone-400 text-xs"> / {fmtQty(lot.lot_quantity)}</span>
                  )}
                </TableCell>
                <TableCell className="font-body text-xs text-stone-600">{lot.responsible || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-1 border-emerald-300 text-emerald-900 h-7 px-2" onClick={() => onLabel(lot)} data-testid={`lot-label-${lot.id}`}>
                    <QrCode className="h-3.5 w-3.5 mr-1" /> Etiqueta
                  </Button>
                  {canManage && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onAdjust(lot)} data-testid={`lot-adjust-${lot.id}`}>Ajustar</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => onDeleteLot(lot)} data-testid={`lot-delete-${lot.id}`}>Excluir</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ProductTable({ products, catInfo, catIcon, canManage, onEntry, onOut, onLabel, onEdit, onDelete, onReload }) {
  const [expanded, setExpanded] = useState({});
  const [adjustLot, setAdjustLot] = useState(null);
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const deleteLot = async (lot) => {
    if (!window.confirm(`Excluir o lote ${lot.batch || "s/ nº"} de ${lot.name}? Os movimentos deste lote também serão removidos.`)) return;
    try {
      await api.delete(`/sanitary/items/${lot.id}`);
      toast.success("Lote removido");
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover lote");
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead className="w-8" />
            <TableHead>Produto</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Saldo total</TableHead>
            <TableHead>Lotes</TableHead>
            <TableHead>Próxima validade</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-stone-500 py-10">Nenhum produto cadastrado</TableCell>
            </TableRow>
          )}
          {products.map((p) => {
            const c = catInfo(p.category);
            const Icon = catIcon(p.category);
            const isOpen = !!expanded[p.id];
            return (
              <Fragment key={p.id}>
                <TableRow className="cursor-pointer hover:bg-stone-50" onClick={() => toggle(p.id)} data-testid={`product-row-${p.id}`}>
                  <TableCell className="pr-0 text-stone-400">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </TableCell>
                  <TableCell>
                    <div className="font-display font-semibold flex items-center gap-2">
                      {p.name}
                      {p.low && <Badge className="bg-rose-100 text-rose-800 border-none text-[10px]">estoque baixo</Badge>}
                    </div>
                    <div className="text-xs text-stone-500 line-clamp-1">{[p.brand, p.sku, p.location].filter(Boolean).join(" · ")}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${c.tone} border-none`}><Icon className="h-3 w-3 mr-1" /> {c.label}</Badge>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <span className={p.low ? "text-rose-700 font-bold" : "text-stone-900 font-semibold"} data-testid={`product-total-${p.id}`}>
                      {fmtQty(p.quantity)} {p.unit}
                    </span>
                    {p.min_quantity > 0 && <span className="text-stone-400 text-xs"> / min {fmtQty(p.min_quantity)}</span>}
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <span className="inline-flex items-center gap-1 text-stone-700" data-testid={`product-lots-count-${p.id}`}>
                      <Layers className="h-3.5 w-3.5 text-stone-400" /> {p.lots_count}
                    </span>
                  </TableCell>
                  <TableCell className="font-body text-xs text-stone-600"><ExpiryCell expiresAt={p.next_expires_at} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {canManage && (
                      <Button size="sm" className="mr-1.5 bg-emerald-800 hover:bg-emerald-900 text-white h-8" onClick={() => onEntry(p)} data-testid={`product-entry-${p.id}`}>
                        <ArrowDownToLine className="h-3.5 w-3.5 mr-1" /> Entrada
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="mr-1.5 border-rose-300 text-rose-800 hover:bg-rose-50 h-8" onClick={() => onOut(p)} disabled={!p.quantity} data-testid={`product-out-${p.id}`}>
                      <ArrowUpFromLine className="h-3.5 w-3.5 mr-1" /> Saída
                    </Button>
                    <Button size="sm" variant="outline" className="mr-1.5 border-stone-300 h-8" onClick={() => (p.lots?.[0] ? onLabel(p.lots[0]) : toggle(p.id))} data-testid={`product-label-${p.id}`}>
                      <QrCode className="h-3.5 w-3.5" />
                    </Button>
                    {canManage && (
                      <>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => onEdit(p)} data-testid={`product-edit-${p.id}`}>Editar</Button>
                        <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onDelete(p)} data-testid={`product-delete-${p.id}`}>Excluir</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                    <TableCell colSpan={7} className="p-0">
                      <LotsPanel product={p} onLabel={onLabel} onAdjust={setAdjustLot} onDeleteLot={deleteLot} canManage={canManage} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {adjustLot && <LotAdjustDialog lot={adjustLot} onOpenChange={() => setAdjustLot(null)} onDone={onReload} />}
    </>
  );
}
